import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, ArrowRight, TrendingUp, Users, Globe, Smartphone, ShoppingCart, Share2, Store, Phone, Search, Video, GraduationCap, Languages, Calculator, BookOpen, Clock, MessageCircle, QrCode, Link2, Package } from 'lucide-react'
import CallbackModal from './CallbackModal'
import CartButton from './CartButton'
import CartModal from './CartModal'
import { useLanguage } from '../contexts/LanguageContext'
import { useCart } from '../contexts/CartContext'
import BrandPronunciationButton from './BrandPronunciationButton'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false)
  const [isMobileSubmenuOpen, setIsMobileSubmenuOpen] = useState(false)
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const location = useLocation()
  const { language, setLanguage, t } = useLanguage()
  const { items } = useCart()
  
  // Show cart button on consulting and education pages (with items on education)
  const enableCartPages = location.pathname.startsWith('/consulting') || location.pathname.startsWith('/education')
  const showCart = enableCartPages && (location.pathname.startsWith('/education') ? items.length > 0 : true)
  
  // Use wider navbar on consulting/education pages
  const isConsultingPage = location.pathname.startsWith('/consulting') || location.pathname.startsWith('/education')

  const isActive = (path: string) => {
    return location.pathname === path
  }

  const getMenuToggleLabel = (open: boolean) => {
    if (language === 'tr') return open ? 'Menüyü kapat' : 'Menüyü aç'
    if (language === 'ru') return open ? 'Закрыть меню' : 'Открыть меню'
    return open ? 'Close menu' : 'Open menu'
  }

  const navigation = [
    { name: t('nav.home'), href: '/', key: 'home' },
    {
      name: t('nav.services'),
      href: '/services',
      key: 'services',
      submenu: [
        { 
          name: t('services.googleAds'), 
          href: '/services/google-ads',
          icon: TrendingUp,
          description: t('serviceDescriptions.googleAds'),
          key: 'googleAds'
        },
        { 
          name: t('services.metaAds'), 
          href: '/services/meta-ads',
          icon: Users,
          description: t('serviceDescriptions.metaAds'),
          key: 'metaAds'
        },
        { 
          name: t('services.socialMedia'), 
          href: '/services/social-media',
          icon: Share2,
          description: t('serviceDescriptions.socialMedia'),
          key: 'socialMedia'
        },
        { 
          name: t('services.webDesign'), 
          href: '/services/web-design',
          icon: Globe,
          description: t('serviceDescriptions.webDesign'),
          key: 'webDesign'
        },
        { 
          name: t('services.seoGeo'), 
          href: '/services/seo-geo',
          icon: Search,
          description: t('serviceDescriptions.seoGeo'),
          key: 'seoGeo'
        },
        { 
          name: t('services.appDevelopment'), 
          href: '/services/app-development',
          icon: Smartphone,
          description: t('serviceDescriptions.appDevelopment'),
          key: 'appDevelopment'
        },
        { 
          name: t('services.marketplace'), 
          href: '/services/marketplace',
          icon: ShoppingCart,
          description: t('serviceDescriptions.marketplace'),
          key: 'marketplace'
        },
        { 
          name: t('services.shopify'), 
          href: '/shopify',
          icon: Store,
          description: t('serviceDescriptions.shopify'),
          key: 'shopify'
        },
        { 
          name: t('services.ugcInfluencer'), 
          href: '/services/ugc-influencer',
          icon: Video,
          description: t('serviceDescriptions.ugcInfluencer'),
          key: 'ugcInfluencer'
        },
        { 
          name: t('services.mentorship'), 
          href: '/services/mentorluk',
          icon: GraduationCap,
          description: t('serviceDescriptions.mentorship'),
          key: 'mentorship'
        },
        { 
          name: t('services.marketingTools'), 
          href: '/marketing-tools',
          icon: Calculator,
          description: t('serviceDescriptions.marketingTools'),
          key: 'marketingTools'
        },
        { 
          name: language === 'tr' ? 'WhatsApp Link Oluşturucu' : language === 'en' ? 'WhatsApp Link Generator' : 'Генератор ссылок WhatsApp',
          href: '/whatsapp-link-generator',
          icon: MessageCircle,
          description: language === 'tr' ? 'Telefon numaranız için özel WhatsApp mesaj linkleri oluşturun' : language === 'en' ? 'Create custom WhatsApp message links' : 'Создавайте пользовательские ссылки для сообщений WhatsApp',
          key: 'whatsappLinkGenerator'
        },
        { 
          name: language === 'tr' ? 'QR Kod Oluşturucu' : language === 'en' ? 'QR Code Generator' : 'Генератор QR-кодов',
          href: '/qr-code-generator',
          icon: QrCode,
          description: language === 'tr' ? 'Herhangi bir metin veya link için QR kod oluşturun' : language === 'en' ? 'Create a QR code for any text or link' : 'Создайте QR-код для любого текста или ссылки',
          key: 'qrCodeGenerator'
        },
        {
          name: language === 'tr' ? 'UTM Link Oluşturucu' : language === 'en' ? 'UTM Link Generator' : 'Генератор UTM-ссылок',
          href: '/utm-link-generator',
          icon: Link2,
          description: language === 'tr' ? 'Kampanya takibi için UTM parametreli linkler oluşturun' : language === 'en' ? 'Create UTM parameterized links for campaign tracking' : 'Создавайте ссылки с UTM-параметрами для отслеживания кампаний',
          key: 'utmLinkGenerator'
        },
        {
          name: language === 'tr' ? 'Desi Hesaplama' : language === 'en' ? 'Desi Calculator' : 'Калькулятор деси',
          href: '/desi-calculator',
          icon: Package,
          description: language === 'tr' ? 'Kargo ve lojistik için paket desi değerini hesaplayın' : language === 'en' ? 'Calculate package desi value for cargo and logistics' : 'Рассчитайте значение деси посылки для грузовых перевозок',
          key: 'desiCalculator'
        },
        { 
          name: t('nav.glossary'), 
          href: '/glossary',
          icon: BookOpen,
          description: language === 'tr' ? 'Dijital pazarlama terimleri sözlüğü' : 'Digital marketing terms glossary',
          key: 'glossary'
        },
        { 
          name: t('nav.education'), 
          href: '/education',
          icon: GraduationCap,
          description: language === 'tr' ? 'Dijital pazarlama eğitimleri ve sertifikalı programlar' : 'Digital marketing training and certified programs',
          key: 'education'
        },
        { 
          name: t('nav.consulting'), 
          href: '/consulting',
          icon: Clock,
          description: language === 'tr' ? 'Saatlik dijital pazarlama danışmanlığı' : 'Hourly digital marketing consulting',
          key: 'consulting'
        },
      ]
    },
    { name: t('nav.blog'), href: '/blog', key: 'blog' },
    { name: t('nav.academy'), href: '/academy', key: 'academy' },
    { name: t('nav.about'), href: '/about', key: 'about' },
    { name: t('nav.references'), href: '/references', key: 'references' },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false)
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.group/lang')) {
        setIsLangMenuOpen(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)
    document.addEventListener('click', handleClickOutside)
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      <div className={`mx-auto px-4 sm:px-6 lg:px-10 pt-4 ${(language === 'ru' || language === 'tr') ? 'max-w-[1400px]' : 'max-w-7xl'}`}>
        <div className={`flex justify-between items-center h-16 transition-all duration-500 ${
          isScrolled 
            ? `bg-white backdrop-blur-md rounded-2xl shadow-lg ${(language === 'ru' || language === 'tr') ? 'px-10' : 'px-8'} h-14` 
            : 'bg-transparent'
        }`}>
          {/* Logo + Pronunciation */}
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center">
              <img 
                src="/images/logo_siyah.webp" 
                alt="Thinklyze" 
                className="h-10 transition-all duration-300"
              />
            </Link>
            <BrandPronunciationButton className="px-1.5 py-1 border-transparent bg-transparent shadow-none text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-offset-0" />
            </div>

          {/* Desktop Navigation */}
          <nav className={`hidden lg:flex items-center transition-all duration-500 ${
            language === 'ru' || language === 'tr'
              ? (isScrolled ? 'space-x-6' : 'space-x-8')
              : (isScrolled ? 'space-x-10' : 'space-x-12')
          }`}>
            {navigation.map((item, index) => (
              <div 
                key={item.key || item.href}
                className="transition-all duration-500"
                style={{
                  transitionDelay: `${index * 30}ms`
                }}
              >
                {item.submenu ? (
                  <div className="relative group/menu">
                    <Link
                      to={item.href}
                      className={`${
                        isActive(item.href)
                          ? 'text-gray-900 font-medium'
                          : 'text-gray-700 hover:text-gray-900'
                      } text-sm transition-all duration-300 flex items-center gap-1 whitespace-nowrap`}
                    >
                      {item.name}
                      <ChevronDown className="w-4 h-4" />
                    </Link>

                    <div className={`fixed left-1/2 -translate-x-1/2 ${isScrolled ? 'top-[80px]' : 'top-[88px]'} ${(language === 'ru' || language === 'tr') ? 'w-[1200px]' : 'w-[1080px]'} max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-gray-100 p-4 opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-300 transform group-hover/menu:translate-y-0 translate-y-2 group/menu`}>
                      <div className="grid grid-cols-3 gap-3.5">
                        {item.submenu.map((subItem) => {
                          const Icon = subItem.icon
                          const isConsulting = subItem.key === 'consulting'
                          const isMarketingTools = subItem.key === 'marketingTools'
                          return (
                        <Link
                              key={subItem.key || subItem.href}
                          to={subItem.href}
                              className={`group/item flex items-center gap-3.5 px-3.5 py-5 rounded-lg border transition-all duration-200 relative ${
                                isConsulting 
                                  ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50 hover:border-blue-400 hover:shadow-md' 
                                  : isMarketingTools
                                  ? 'border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 hover:border-orange-400 hover:shadow-md'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                        >
                              {isConsulting && (
                                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                                  {language === 'tr' ? 'POPÜLER' : language === 'ru' ? 'ПОПУЛЯРНО' : 'POPULAR'}
                                </div>
                              )}
                              {isMarketingTools && (
                                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                                  {language === 'tr' ? 'ÜCRETSİZ' : language === 'ru' ? 'БЕСПЛАТНО' : 'FREE'}
                                </div>
                              )}
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                                isConsulting 
                                  ? 'bg-gradient-to-br from-blue-500 to-blue-600 group-hover/item:from-blue-600 group-hover/item:to-blue-700' 
                                  : isMarketingTools
                                  ? 'bg-gradient-to-br from-orange-500 to-orange-600 group-hover/item:from-orange-600 group-hover/item:to-orange-700'
                                  : 'bg-gray-100 group-hover/item:bg-blue-100'
                              }`}>
                                <Icon className={`w-4 h-4 transition-colors duration-200 ${
                                  isConsulting || isMarketingTools
                                    ? 'text-white' 
                                    : 'text-gray-700 group-hover/item:text-blue-600'
                                }`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium transition-colors duration-200 mb-0.5 text-xs leading-tight ${
                                  isConsulting 
                                    ? 'text-blue-700 group-hover/item:text-blue-800' 
                                    : isMarketingTools
                                    ? 'text-orange-700 group-hover/item:text-orange-800'
                                    : 'text-gray-900 group-hover/item:text-blue-600'
                                }`}>
                          {subItem.name}
                                </div>
                                <div className="text-[10px] text-gray-600 leading-tight">
                                  {subItem.description}
                                </div>
                              </div>
                        </Link>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`${
                      isActive(item.href)
                        ? 'text-gray-900 font-medium'
                        : 'text-gray-700 hover:text-gray-900'
                    } text-sm transition-all duration-300 whitespace-nowrap`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Right side icons and button */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative group/lang">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  isScrolled
                    ? 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'
                    : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Languages className="w-4 h-4" />
                <span className="uppercase">{language}</span>
              </button>
              {isLangMenuOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[120px] z-50">
                  <button
                    onClick={() => {
                      setLanguage('tr')
                      setIsLangMenuOpen(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      language === 'tr' ? 'bg-gray-50 font-medium' : ''
                    }`}
                  >
                    Türkçe
                  </button>
                  <button
                    onClick={() => {
                      setLanguage('en')
                      setIsLangMenuOpen(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      language === 'en' ? 'bg-gray-50 font-medium' : ''
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => {
                      setLanguage('ru')
                      setIsLangMenuOpen(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      language === 'ru' ? 'bg-gray-50 font-medium' : ''
                    }`}
                  >
                    Русский
            </button>
                </div>
              )}
            </div>
            
            {/* Callback Button */}
            <button
              onClick={() => setIsCallbackModalOpen(true)}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                isScrolled
                  ? 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'
                  : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Phone className="w-4 h-4" />
              {t('callback.callMe')}
            </button>
            
            {/* CTA Button */}
            {showCart && (
              <CartButton onClick={() => setIsCartOpen(true)} />
            )}
            <Link
              to="/contact"
              className="bg-black text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-all duration-300 flex items-center gap-2"
            >
              {t('nav.contact')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-2">
            {showCart && (
              <CartButton onClick={() => setIsCartOpen(true)} />
            )}
          <button
              type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={getMenuToggleLabel(isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
              className={`p-2 rounded-md transition-colors duration-300 ${
                isMenuOpen || isScrolled
                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' 
                  : 'text-gray-900 hover:text-black hover:bg-gray-100'
              }`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            id="mobile-navigation"
            className="lg:hidden bg-white rounded-2xl shadow-lg border border-gray-100 mt-2 py-4 mx-4 max-h-[calc(100vh-120px)] overflow-y-auto overscroll-contain scrollbar-hide"
          >
            <nav className="flex flex-col space-y-4 px-4">
              {navigation.map((item) => (
                <div key={item.key || item.href}>
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => setIsMobileSubmenuOpen(!isMobileSubmenuOpen)}
                        className={`${
                          isActive(item.href)
                            ? 'text-gray-900 font-medium'
                            : 'text-gray-600 hover:text-gray-900'
                        } w-full flex items-center justify-between py-2 text-sm transition-colors duration-200`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                          isMobileSubmenuOpen ? 'transform rotate-180' : ''
                        }`} />
                      </button>
                      {isMobileSubmenuOpen && (
                        <div className="ml-4 mt-2 space-y-2">
                          {item.submenu.map((subItem) => {
                            const Icon = subItem.icon
                            const isConsulting = subItem.key === 'consulting'
                            const isMarketingTools = subItem.key === 'marketingTools'
                            return (
                            <Link
                              key={subItem.key || subItem.href}
                              to={subItem.href}
                              className={`flex items-center gap-3 p-3 rounded-lg text-sm transition-all duration-200 relative ${
                                isConsulting
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 text-blue-700 font-medium hover:border-blue-300 hover:shadow-sm'
                                  : isMarketingTools
                                  ? 'bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 text-orange-700 font-medium hover:border-orange-300 hover:shadow-sm'
                                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                              }`}
                              onClick={() => {
                                setIsMenuOpen(false)
                                setIsMobileSubmenuOpen(false)
                              }}
                            >
                              {isConsulting && (
                                <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                                  {language === 'tr' ? 'POPÜLER' : language === 'ru' ? 'ПОПУЛЯРНО' : 'POPULAR'}
                                </div>
                              )}
                              {isMarketingTools && (
                                <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-orange-600 to-orange-700 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                                  {language === 'tr' ? 'ÜCRETSİZ' : language === 'ru' ? 'БЕСПЛАТНО' : 'FREE'}
                                </div>
                              )}
                              <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isConsulting 
                                  ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                                  : isMarketingTools
                                  ? 'bg-gradient-to-br from-orange-500 to-orange-600'
                                  : 'bg-transparent'
                              }`}>
                                <Icon className={`w-4 h-4 ${isConsulting || isMarketingTools ? 'text-white' : 'text-gray-400'}`} />
                              </div>
                              <span>{subItem.name}</span>
                            </Link>
                            )
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                  <Link
                    to={item.href}
                    className={`${
                      isActive(item.href)
                        ? 'text-gray-900 font-medium'
                        : 'text-gray-600 hover:text-gray-900'
                    } block py-2 text-sm transition-colors duration-200`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  )}
                </div>
              ))}
              <div className="mt-6 border-t border-gray-200 pt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setLanguage('tr')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      language === 'tr'
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    TR
                  </button>
                  <button
                    onClick={() => setLanguage('en')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      language === 'en'
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage('ru')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      language === 'ru'
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    RU
                  </button>
                </div>

                <button
                  onClick={() => setIsCallbackModalOpen(true)}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {t('callback.callMe')}
                </button>

                <Link
                  to="/contact"
                  className="w-full bg-black text-white px-4 py-3 rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.contact')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Callback Modal */}
      <CallbackModal 
        isOpen={isCallbackModalOpen} 
        onClose={() => setIsCallbackModalOpen(false)} 
      />

      {/* Cart Modal */}
      {showCart && (
        <CartModal 
          isOpen={isCartOpen} 
          onClose={() => setIsCartOpen(false)}
        />
      )}
    </header>
  )
}

export default Header










